import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

function normalizeCircadianPayload(data = {}) {
  const currentEnergy = data.current_energy ?? data.currentEnergy ?? 100.0;
  let currentState = data.current_state ?? data.currentState ?? 'ALERT';

  if (currentState === 'AWAKE' || currentEnergy >= 100.0) {
    currentState = 'ALERT';
  }

  return {
    currentEnergy,
    currentState
  };
}

/**
 * useCircadianState
 * Hook para monitorear el estado de energía (stamina) del agente en tiempo real.
 * Realiza un polling ligero cada 30 segundos, acelerando a 10s si el estado es 'SLEEPING'.
 */
export function useCircadianState() {
  const [stamina, setStamina] = useState({
    currentEnergy: 100.0,
    currentState: 'ALERT'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStamina = useCallback(async () => {
    try {
      const res = await api.get('/agent/stamina');

      if (res.data) {
        setStamina(normalizeCircadianPayload(res.data));
      }
    } catch (err) {
      console.error('[useCircadianState] Error fetching agent stamina:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Carga inicial
    fetchStamina();
  }, [fetchStamina]);

  useEffect(() => {
    const handleEnergyUpdated = () => {
      fetchStamina();
    };

    window.addEventListener('q:energy-updated', handleEnergyUpdated);
    return () => window.removeEventListener('q:energy-updated', handleEnergyUpdated);
  }, [fetchStamina]);

  useEffect(() => {
    // Limpiar intervalo previo
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Determinar delay: 10s si está durmiendo (para despertar rápido), 30s por defecto.
    const delay = stamina.currentState === 'SLEEPING' ? 10000 : 30000;

    intervalRef.current = setInterval(fetchStamina, delay);

    return () => clearInterval(intervalRef.current);
  }, [stamina.currentState, fetchStamina]);

  return {
    ...stamina,
    loading,
    error,
    refresh: fetchStamina,
    refreshCircadianState: fetchStamina
  };
}

export default useCircadianState;
